import { stateOptions } from '../commonStateTaxData';

export const step1Questions = [
  {
    id: 'employee_name',
    title: 'Full name',
    is_required: true,
    question_type: 'short_text',
  },
  {
    id: 'employee_ssn',
    title: 'Social Security number',
    is_required: true,
    placeholder: '9 digits',
    question_type: 'identity',
    identity_type: 'ssn',
    rules: [
      {
        category: 'regex',
        action: 'match',
        value: '^\\d{9}$',
      },
    ],
  },
  {
    id: 'street_address',
    title: 'Home address',
    is_required: true,
    question_type: 'short_text',
  },
  {
    id: 'city_or_town',
    title: 'City or town',
    is_required: true,
    question_type: 'short_text',
  },
  {
    id: 'state',
    title: 'State',
    is_required: true,
    question_type: 'dropdown',
    options: stateOptions,
  },
  {
    id: 'zip_code',
    title: 'Zip code',
    is_required: true,
    question_type: 'short_text',
    placeholder: '5 digits',
    rules: [
      {
        category: 'regex',
        action: 'match',
        value: '^\\d{5}$',
      },
    ],
  },
  {
    id: 'filing_status',
    title: 'Please select filing status:',
    is_required: true,
    question_type: 'multiple_choice',
    options: [
      {
        codeValue: 'single',
        shortName: 'Single or married but legally separated',
      },
      {
        codeValue: 'married',
        shortName: 'Married',
      },
    ],
  },
];

export const initialAllowancesQuestions = [
  {
    id: 'personal_allowances',
    title: 'Personal allowances:',
    question_type: 'short_text',
    rules: [
      {
        category: 'regex',
        action: 'match',
        value: '^(0|[1-9][0-9]*)$',
      },
    ],
  },
  {
    id: 'dependents',
    title: 'Allowances for dependents. You may claim 1 allowance for each dependent you claim on your Iowa income tax return.',
    question_type: 'short_text',
    rules: [
      {
        category: 'regex',
        action: 'match',
        value: '^(0|[1-9][0-9]*)$',
      },
      {
        value: '100',
        action: 'lte',
        category: 'number',
      },
    ],
  },
  {
    id: 'itemized_deductions',
    title: 'Allowances for itemized deductions. For instructions, see Form IA W-4.',
    question_type: 'short_text',
    rules: [
      {
        category: 'regex',
        action: 'match',
        value: '^(0|[1-9][0-9]*)$',
      },
    ],
  },
  {
    id: 'adjustments_to_income',
    title: 'Allowances for adjustments to income. Estimate allowable adjustments to income for payments such as an IRA, Keogh, or SEP; penalty on early withdrawal of savings; and student loan interest, which are reflected on the IA 1040. Divide this amount by $600, round to the nearest whole number, and enter here:',
    question_type: 'short_text',
    rules: [
      {
        category: 'regex',
        action: 'match',
        value: '^(0|[1-9][0-9]*)$',
      },
    ],
  },
  {
    id: 'child_and_dependent_care_credit',
    title: 'Allowances for child and dependent care credit:',
    question_type: 'short_text',
    rules: [
      {
        category: 'regex',
        action: 'match',
        value: '^(0|[1-9][0-9]*)$',
      },
    ],
  },
  {
    id: 'additional_amount',
    title: 'Additional amount, if any, you want deducted each pay period:',
    question_type: 'short_text',
    rules: [
      {
        category: 'regex',
        action: 'match',
        value: '^[0-9]+(.[0-9]{1,2})?$',
      },
    ],
  },
];

export const stateOfDomicileInitialQuestion = [
  {
    id: 'state_of_domicile',
    title: 'Select your state of domicile or residence:',
    question_type: 'dropdown',
    is_required: true,
    options: stateOptions,
  },
];

export const getStep1Questions = (version = '') => {
  switch (version) {
    case '11_28_2023':
      return step1Questions.map((question) => {
        if (question.id === 'filing_status') {
          return {
            ...question,
            options: [
              {
                codeValue: 'other',
                shortName: 'Other',
              },
              {
                codeValue: 'head_of_household',
                shortName: 'Head of Household',
              },
              {
                codeValue: 'married_filing_jointly',
                shortName: 'Married filing jointly',
              },
            ],
          };
        }
        return question;
      });
    case '11_18_2024':
      return step1Questions.map((question) => {
        if (question.id === 'filing_status') {
          return {
            ...question,
            options: [
              {
                codeValue: 'other_incl_single',
                shortName: 'Other (Including Single)',
              },
              {
                codeValue: 'married_filing_jointly_or_qualifying_surviving_spouse',
                shortName: 'Married filing jointly or Qualifying Surviving Spouse',
              },
              {
                codeValue: 'head_of_household',
                shortName: 'Head of Household',
              },
            ],
          };
        }
        return question;
      });
    default:
      return step1Questions;
  }
};

export const getInitialAllowancesQuestions = (version = '') => {
  switch (version) {
    case '11_28_2023':
    case '11_18_2024':
      return initialAllowancesQuestions.map((question) => {
        if (question.id === 'personal_allowances') {
          return {
            ...question,
            title: 'Personal allowances. For instructions, see Form IA W-4.',
          };
        }

        if (question.id === 'child_and_dependent_care_credit') {
          return {
            ...question,
            title: 'Allowances for child and dependent care credit. For instructions, see Form IA W-4.',
          };
        }

        if (question.id === 'dependents') {
          return {
            ...question,
            title: 'Allowances for dependents. You may claim $40 for each dependent you claim on your Iowa income tax return.',
          };
        }

        if (question.id === 'adjustments_to_income') {
          return {
            ...question,
            title: 'Allowances for adjustments to income. Estimate allowable adjustments to income for payments such as an IRA, Keogh, or SEP; penalty on early withdrawal of savings; and student loan interest, which are reflected on the IA 1040. Divide this amount by 15, round to the nearest whole number, and enter here:',
          };
        }

        return question;
      });
    default:
      return initialAllowancesQuestions;
  }
};

export const getExemptionOptions = (version = '') => {
  const baseOptions = {
    exemption_from_withholding_one: 'You do not expect to owe any Iowa income tax and have a right to a full refund of ALL income tax withheld.',
    exemption_from_withholding_two: 'You are claiming an exemption from Iowa income tax as a military spouse based on the Military Spouses Residency Relief Act of 2009 or the Veterans Benefits and Transition Act of 2018.',
  };

  if (['11_18_2024'].includes(version)) {
    baseOptions.exemption_from_withholding_two = 'You are claiming an exemption from Iowa income tax as a military spouse based on the Military Spouses Residency Relief Act, the Veterans Benefits and Transition Action, or the Veterans Auto and Education Improvement Act of 2022.';
  }

  return baseOptions;
};
